import { useState, useEffect } from 'react';
import { Menu, X, Network, ArrowRight } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import { useTheme } from "next-themes";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

interface NavLinkProps {
  href: string;
  title: string;
  description: string;
  featured?: boolean;
  icon?: React.ReactNode;
  external?: boolean;
}

const NavLink: React.FC<NavLinkProps> = ({ href, title, description, featured, icon }) => (
  <NavigationMenuLink asChild>
    <a
      href={href}
      className={`block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors
        ${featured
          ? 'bg-gradient-to-r from-primary/10 to-background border-l-2 border-primary'
          : 'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground'
        }`}
    >
      <div className="flex items-center gap-2 mb-1">
        {icon && <div className="shrink-0">{icon}</div>}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium leading-none">{title}</span>
          {featured &&
            <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
              New
            </span>
          }
        </div>
      </div>
      <p className="line-clamp-2 text-sm leading-snug text-muted-foreground mt-1">
        {description}
      </p>
      {featured && (
        <div className="mt-3 flex items-center text-xs text-primary">
          <span>Learn more</span>
          <ArrowRight className="ml-1 h-3 w-3" />
        </div>
      )}
    </a>
  </NavigationMenuLink>
);

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const { theme } = useTheme();

  useEffect(() => {
    const handleScroll = (): void => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const productLinks: NavLinkProps[] = [
    {
      href: "https://bitcoin-sv.github.io/metanet-desktop",
      title: "Metanet Desktop",
      description: "Seamless, user-friendly software enabling consumer access to the Metanet experience.",
      featured: true,
      icon: <Network className="h-4 w-4 text-primary" />
    },
    {
      href: "/overlay-hosting-services",
      title: "Overlay Hosting Services",
      description: "Managed cloud environment ensuring reliable, scalable, and secure blockchain infrastructure.",
    },
    {
      href: "/wallet-storage-management",
      title: "Wallet Storage & Management",
      description: "Robust wallet storage solutions coupled with efficient management of digital assets.",
    },
    {
      href: "/chain-backed-storage-services",
      title: "Chain Backed Storage Services",
      description: "Ensuring data integrity and redundancy at reduced cost, backed by a resilient blockchain.",
    },
    {
      href: "/identity-verification-services",
      title: "Identity Verification Services",
      description: "Issuing trusted certificates for increased confidence and fraud prevention with every interaction.",
    },
    // {
    //   href: "/onboarding-offboarding",
    //   title: "Onboarding and Offboarding",
    //   description: "Easy, compliant, and low-friction experiences to acquire and relinquish BSV Blockchain assets.",
    // },
    {
      href: "/hosted-p2p-message-box",
      title: "Hosted P2P Message Box",
      description: "Facilitating distributed peer interactions, even in challenging network topologies.",
    },
    // {
    //   href: "/immutable-logcapture",
    //   title: "Immutable LogCapture",
    //   description: "Reliable service providing unalterable, secure data logging for compliance and auditing purposes.",
    // },
    // {
    //   href: "/secure-teleforms-generator",
    //   title: "Secure TeleForms Generator",
    //   description: "Easily create encrypted forms with Fill Once Technology for users and compliance for organizations.",
    // },
    // {
    //   href: "/document-signing-system",
    //   title: "Document Signing System",
    //   description: "Metanet Digital Signatures and linked certificates with on-chain authenticity, security, and compliance.",
    // },
    {
      href: "/metanet-engineering-curriculum",
      title: "Metanet Engineering Curriculum",
      description: "Comprehensive educational curriculum designed to equip engineers with in-depth blockchain skills.",
    }
  ];

  const solutionLinks: NavLinkProps[] = [
    {
      href: "/blockchain-training-enterprise-engineers",
      title: "Blockchain Training for Enterprise Engineers",
      description: "Tailored training programs designed to upskill engineers for implementing and managing blockchain technologies within enterprise environments.",
    },
    {
      href: "/financial-trading-tokenization",
      title: "Financial, Trading, and Tokenization",
      description: "End-to-end blockchain solutions designed for secure financial transactions, asset tokenization, and trading platforms.",
    },
    {
      href: "/archival-recordkeeping",
      title: "Archival and Recordkeeping",
      description: "From property deeds to death records, our offerings are designed to stand the test of time.",
    },
    {
      href: "/skilled-industries",
      title: "Skilled Industries",
      description: "From digital logbooks for professional divers to tracing car parts on the assembly line, we got you covered!",
    },
    {
      href: "/provenance",
      title: "Provenance",
      description: "Transparent tracking solutions providing traceability and verification for products, supply chains, and assets.",
    },
    {
      href: "/api-platform-monetization",
      title: "API & Platform Monetization",
      description: "Micropayments Architecture Services designed to grow your business, expand your reach, and reduce friction.",
    },
    {
      href: "/smart-contract-development",
      title: "Smart Contract Development",
      description: "Let us do the heavy lifting so you can focus on building more efficient interactions.",
    },
    {
      href: "/provably-fair-gaming-architecture",
      title: "Provably Fair Gaming Architecture Development Solutions",
      description: "Games should be fair, and with blockchain, they can be. Let us design solutions that empower players with greater trust and transparency.",
    },
    {
      href: "/blockchain-integration-consultancy",
      title: "Blockchain Integration / Network Architecture consultancy",
      description: "Pioneering research into scalable, resilient base layer systems backed by world class expertise.",
    }
  ];

  const resourceLinks: NavLinkProps[] = [
    {
      href: "https://docs.projectbabbage.com",
      title: "Developer Documentation",
      description: "Comprehensive guides and API references",
      external: true
    },
    // {
    //   href: "https://blog.projectbabbage.com/blog",
    //   title: "Blog",
    //   description: "News, insights, and updates from the Babbage team.",
    //   external: true
    // },
    // {
    //   href: "#",
    //   title: "In the News",
    //   description: "Mentions and articles about Babbage."
    // },
    {
      href: "https://join.slack.com/t/projectbabbage/shared_invite/zt-29gkwwaoj-yFqgWEC4YT_Sq7eH5Expsw",
      title: "Metanet Dev Community",
      description: "Connect with developers on Slack.",
      external: true
    },
    {
      href: "https://babbageconnect.com",
      title: "Babbage Connect",
      description: "Get your startup funded!",
      external: true
    },
    {
      href: "https://github.com/bitcoin-sv/BRCs",
      title: "Open Metanet Standards Repository (BRCs)",
      description: "Explore the latest BRC specifications.",
      external: true
    },
    // {
    //   href: "#",
    //   title: "Technology Case Studies & Examples",
    //   description: "See how Babbage technology is used in the real world."
    // },
    // {
    //   href: "#",
    //   title: "Testimonials",
    //   description: "Hear from our satisfied clients and partners."
    // },
    // {
    //   href: "#",
    //   title: "Metanet Certified Organizations Program",
    //   description: "Validating expertise and adherance to open Metanet protocols within organizations big and small."
    // }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'glass py-4' : 'py-6'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center">
          <a href="/" className="flex items-center space-x-2 group">
            <div className="relative">
              <img
                src={theme === 'dark' ? '/babb-logo-dark.svg' : '/babb-logo-light.svg'}
                alt="Babbage Logo"
                className="h-8 w-8 relative transform transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 drop-shadow-sm"
              />
            </div>
            <span className="text-lg font-[500] tracking-tight">
              Babbage
            </span>
          </a>

          <div className="hidden md:flex items-center space-x-8">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger>Products</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid gap-3 p-6 md:w-[600px] md:grid-cols-2">
                      {productLinks.map((link, index) => (
                        <NavLink key={index} {...link} />
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger>Solutions</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid gap-3 p-6 md:w-[600px] md:grid-cols-2">
                      {solutionLinks.map((link, index) => (
                        <NavLink key={index} {...link} />
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger>Resources</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid gap-3 p-6 w-[400px]">
                      {resourceLinks.map((link, index) => (
                        <NavLink key={index} {...link} />
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <a href="/about" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">About</a>
            <a href="/contact" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">Contact</a>
            <ThemeToggle />
          </div>

          <div className="md:hidden flex items-center gap-4">
            <ThemeToggle />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden glass mt-2 py-4">
          <div className="flex flex-col items-center space-y-4">
            <div className="flex flex-col items-center space-y-2">
              <span className="font-medium">Products</span>
              {productLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  className="text-sm text-gray-600 dark:text-gray-400"
                >
                  {link.title}
                </a>
              ))}
            </div>
            <div className="flex flex-col items-center space-y-2">
              <span className="font-medium">Solutions</span>
              {solutionLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  className="text-sm text-gray-600 dark:text-gray-400"
                >
                  {link.title}
                </a>
              ))}
            </div>
            <div className="flex flex-col items-center space-y-2">
              <span className="font-medium">Resources</span>
              {resourceLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  className="text-sm text-gray-600 dark:text-gray-400"
                >
                  {link.title}
                </a>
              ))}
            </div>
            <a href="/about" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">About</a>
            <a href="/contact" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">Contact</a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
